import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Save, RotateCcw, GripVertical, ChevronDown, ChevronUp, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FOLLOWUP_SETTINGS } from "@/constants/testIds/crm";

const REMINDER_OPTIONS = [
    { v: "none", label: "None" },
    { v: "same_day", label: "Same day" },
    { v: "1_day_before", label: "1 day before" },
    { v: "2_hours_before", label: "2 hours before" },
    { v: "1_hour_before", label: "1 hour before" },
];

const TYPE_OPTIONS = ["email", "call", "whatsapp", "meeting"];

const emptyStep = (n) => ({
    sequence_number: n,
    stage_name: `Follow-up ${n}`,
    days_after_lead: n * 3,
    type: "email",
    email_subject: `Follow-up ${n} — {{company}}`,
    email_body: "Hi {{client_name}},\n\n\n\nBest,\n{{sender_name}}",
    reminder_timing: "same_day",
});

function StepEditor({ step, index, onChange, onRemove, expanded, onToggle }) {
    const patch = (k, v) => onChange({ ...step, [k]: v });
    return (
        <div
            data-testid={`${FOLLOWUP_SETTINGS.stepPrefix}${index}`}
            className="border border-border rounded-md bg-card overflow-hidden"
        >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary/30">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
                <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/10 text-primary px-2 py-0.5 rounded">
                    #{step.sequence_number}
                </span>
                <input
                    value={step.stage_name}
                    onChange={(e) => patch("stage_name", e.target.value)}
                    data-testid={`${FOLLOWUP_SETTINGS.stepStageName}${index}`}
                    className="flex-1 h-8 px-2 rounded-md bg-background border border-input text-sm font-medium"
                    placeholder="Stage name"
                />
                <div className="flex items-center gap-1 text-xs">
                    <span className="text-muted-foreground">Day</span>
                    <input
                        type="number"
                        min={0}
                        value={step.days_after_lead}
                        onChange={(e) => patch("days_after_lead", parseInt(e.target.value || "0", 10))}
                        data-testid={`${FOLLOWUP_SETTINGS.stepDays}${index}`}
                        className="w-16 h-8 px-2 rounded-md bg-background border border-input text-sm text-center tabular-nums"
                    />
                </div>
                <button
                    type="button"
                    onClick={onToggle}
                    className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-secondary"
                    title={expanded ? "Collapse" : "Expand"}
                >
                    {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                <button
                    type="button"
                    onClick={onRemove}
                    data-testid={`${FOLLOWUP_SETTINGS.stepRemove}${index}`}
                    className="h-8 w-8 inline-flex items-center justify-center rounded text-destructive hover:bg-destructive/10"
                    title="Remove step"
                >
                    <Trash2 className="h-4 w-4" />
                </button>
            </div>
            {expanded && (
                <div className="p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Type</div>
                            <select
                                value={step.type}
                                onChange={(e) => patch("type", e.target.value)}
                                className="w-full h-9 px-2 rounded-md bg-background border border-input text-sm capitalize"
                            >
                                {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Reminder</div>
                            <select
                                value={step.reminder_timing || "same_day"}
                                onChange={(e) => patch("reminder_timing", e.target.value)}
                                className="w-full h-9 px-2 rounded-md bg-background border border-input text-sm"
                            >
                                {REMINDER_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Email subject</div>
                        <input
                            value={step.email_subject}
                            onChange={(e) => patch("email_subject", e.target.value)}
                            data-testid={`${FOLLOWUP_SETTINGS.stepSubject}${index}`}
                            className="w-full h-9 px-2 rounded-md bg-background border border-input text-sm"
                        />
                    </div>
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Email body</div>
                        <textarea
                            rows={7}
                            value={step.email_body}
                            onChange={(e) => patch("email_body", e.target.value)}
                            data-testid={`${FOLLOWUP_SETTINGS.stepBody}${index}`}
                            className="w-full px-3 py-2 rounded-md bg-background border border-input text-sm font-mono leading-relaxed"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default function FollowupSettings() {
    const { user } = useAuth();
    const [settings, setSettings] = useState(null);
    const [busy, setBusy] = useState(false);
    const [openIdx, setOpenIdx] = useState(-1);

    const load = async () => {
        const { data } = await api.get("/settings/followup-automation");
        setSettings(data);
    };
    useEffect(() => { load(); }, []);

    if (!user || user.role !== "admin") {
        return (
            <div className="text-sm text-muted-foreground">
                Admin access required to configure follow-up automation.
            </div>
        );
    }
    if (!settings) return <div className="text-sm text-muted-foreground">Loading settings…</div>;

    const patch = (k, v) => setSettings({ ...settings, [k]: v });

    const updateStep = (i, next) => {
        const steps = [...settings.steps];
        steps[i] = next;
        patch("steps", steps);
    };
    const removeStep = (i) => {
        const steps = settings.steps.filter((_, idx) => idx !== i)
            .map((s, idx) => ({ ...s, sequence_number: idx + 1 }));
        patch("steps", steps);
    };
    const addStep = () => {
        const next = [...settings.steps, emptyStep(settings.steps.length + 1)];
        patch("steps", next);
        setOpenIdx(next.length - 1);
    };
    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.put("/settings/followup-automation", {
                enabled: settings.enabled,
                default_time: settings.default_time,
                default_reminder_timing: settings.default_reminder_timing,
                steps: settings.steps.map((s, i) => ({ ...s, sequence_number: i + 1 })),
            });
            setSettings(data);
            toast.success("Settings saved");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to save");
        }
        setBusy(false);
    };
    const restoreDefaults = async () => {
        if (!window.confirm("Restore default sequence (3-day, 7-day, 10-day)?")) return;
        setBusy(true);
        try {
            await api.put("/settings/followup-automation", {
                enabled: true,
                default_time: "09:00",
                default_reminder_timing: "same_day",
                steps: [
                    emptyStep(1),
                    { ...emptyStep(2), days_after_lead: 7 },
                    { ...emptyStep(3), days_after_lead: 10 },
                ],
            });
            await load();
            toast.success("Defaults restored");
        } catch { toast.error("Failed"); }
        setBusy(false);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6 max-w-4xl"
            data-testid={FOLLOWUP_SETTINGS.root}
        >
            <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Automation</div>
                <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Follow-up Scheduler</h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    Configure the automatic follow-up sequence created for every new lead. Templates support
                    <code className="mx-1 px-1.5 py-0.5 rounded bg-secondary text-[11px] font-mono">{"{{company}}"}</code>,
                    <code className="mx-1 px-1.5 py-0.5 rounded bg-secondary text-[11px] font-mono">{"{{client_name}}"}</code>,
                    <code className="mx-1 px-1.5 py-0.5 rounded bg-secondary text-[11px] font-mono">{"{{sender_name}}"}</code> and
                    <code className="mx-1 px-1.5 py-0.5 rounded bg-secondary text-[11px] font-mono">{"{{followup_number}}"}</code>.
                </p>
            </div>

            {/* Global switches */}
            <div className="border border-border rounded-md bg-card p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-sm font-semibold">Auto-schedule on new lead</div>
                        <div className="text-xs text-muted-foreground">When enabled, every new lead gets the sequence below.</div>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!settings.enabled}
                            onChange={(e) => patch("enabled", e.target.checked)}
                            data-testid={FOLLOWUP_SETTINGS.enabledToggle}
                            className="h-4 w-4"
                        />
                        <span className="text-sm">{settings.enabled ? "On" : "Off"}</span>
                    </label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Default time (24h)</div>
                        <input
                            type="time"
                            value={settings.default_time}
                            onChange={(e) => patch("default_time", e.target.value)}
                            data-testid={FOLLOWUP_SETTINGS.defaultTime}
                            className="h-9 w-32 px-2 rounded-md bg-background border border-input text-sm"
                        />
                    </div>
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Default reminder</div>
                        <select
                            value={settings.default_reminder_timing}
                            onChange={(e) => patch("default_reminder_timing", e.target.value)}
                            data-testid={FOLLOWUP_SETTINGS.defaultReminder}
                            className="h-9 px-2 rounded-md bg-background border border-input text-sm"
                        >
                            {REMINDER_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {/* Sequence steps */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Sequence ({settings.steps.length} step{settings.steps.length !== 1 ? "s" : ""})</h2>
                    <button
                        type="button"
                        onClick={addStep}
                        data-testid={FOLLOWUP_SETTINGS.addStep}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border bg-card hover:bg-secondary text-sm transition-colors"
                    >
                        <Plus className="h-4 w-4" /> Add step
                    </button>
                </div>
                {settings.steps.length === 0 && (
                    <div className="text-sm text-muted-foreground italic border border-dashed border-border rounded-md p-6 text-center">
                        No steps configured. Add one to activate the scheduler.
                    </div>
                )}
                <div className="space-y-3">
                    {settings.steps.map((s, i) => (
                        <StepEditor
                            key={i}
                            step={{ ...s, sequence_number: i + 1 }}
                            index={i}
                            onChange={(next) => updateStep(i, next)}
                            onRemove={() => removeStep(i)}
                            expanded={openIdx === i}
                            onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
                        />
                    ))}
                </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-3 sticky bottom-4 bg-background/80 backdrop-blur-xl border border-border rounded-md p-3">
                <button
                    type="button"
                    onClick={restoreDefaults}
                    disabled={busy}
                    data-testid={FOLLOWUP_SETTINGS.restore}
                    className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-border hover:bg-secondary text-sm transition-colors disabled:opacity-60"
                >
                    <RotateCcw className="h-4 w-4" /> Restore defaults
                </button>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Info className="h-3.5 w-3.5" />
                    New leads will use these settings immediately.
                </div>
                <button
                    type="button"
                    onClick={save}
                    disabled={busy}
                    data-testid={FOLLOWUP_SETTINGS.save}
                    className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors disabled:opacity-60"
                >
                    <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save settings"}
                </button>
            </div>
        </motion.div>
    );
}
